import React, { useState } from 'react';
import { generateHansei } from '../services/geminiService';
import { HanseiReflection } from '../types';
import { Layers, RefreshCw, AlertCircle, ArrowRight, ShieldCheck, Search } from 'lucide-react';
import { useUser } from '../context/UserContext';

const KaizenHansei: React.FC = () => {
  const [mistake, setMistake] = useState('');
  const [reflection, setReflection] = useState<HanseiReflection | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handleReflect = async () => {
    if (!mistake.trim()) return;
    setLoading(true);
    setReflection(null);
    try {
      const data = await generateHansei(mistake);
      setReflection(data);
      addXp(50, 'Practiced Hansei');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-slate-50 text-slate-600 rounded-full mb-4">
          <Layers size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Hansei Reflection</h1>
        <p className="text-nunchi-600">
          Turn failure into gold. Hansei is not about shame; it is about objective acknowledgment and improvement.
        </p>
      </header>

      {!reflection && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What went wrong recently?
            </label>
            <textarea
              value={mistake}
              onChange={(e) => setMistake(e.target.value)}
              placeholder="e.g., I missed a deadline, I snapped at my partner, I broke my diet..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-slate-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleReflect}
              disabled={!mistake.trim()}
              className="w-full bg-slate-700 text-white py-4 rounded-xl font-bold hover:bg-slate-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Layers size={18} /> Begin Reflection
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-slate-100 border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-slate-700">Analyzing the root cause...</p>
        </div>
      )}

      {reflection && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white border border-nunchi-200 rounded-2xl overflow-hidden shadow-lg">
                 <div className="bg-slate-50 p-6 border-b border-slate-100">
                     <h3 className="text-slate-500 uppercase tracking-widest text-xs font-bold mb-2 flex items-center gap-2">
                         <AlertCircle size={14} /> Acknowledgment
                     </h3>
                     <p className="text-lg text-nunchi-900 font-serif">"{reflection.acknowledgment}"</p>
                 </div>

                 <div className="p-6">
                     <h3 className="text-blue-500 uppercase tracking-widest text-xs font-bold mb-3 flex items-center gap-2">
                         <Search size={14} /> The Root Cause (5 Whys)
                     </h3>
                     <div className="bg-blue-50 p-4 rounded-xl text-nunchi-800 leading-relaxed mb-6">
                         {reflection.rootCause}
                     </div>

                     <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                         <h3 className="text-green-600 uppercase tracking-widest text-xs font-bold mb-2 flex items-center gap-2">
                             <ShieldCheck size={14} /> Improvement Plan
                         </h3>
                         <p className="text-green-900 font-bold text-lg">{reflection.improvement}</p>
                     </div>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setReflection(null); setMistake(''); }}
                  className="text-slate-600 hover:text-slate-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Reflect Again
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default KaizenHansei;